import React, { useState, useEffect, useRef } from 'react';
import {
    Notification as SparkNotification,
    DropdownMenu,
    Spinner,
    ValidationCard,
    ValidationCardCarousel,
    sparkToast,
} from '@spark';
import ImprovementModeTour from '@/features/datasets/components/DataSelection/ImprovementModeTour';
import ReviewCompleteModal from '@/features/datasets/components/DataSelection/ReviewCompleteModal';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';
import { tableFiltersToImprovementModeLabels } from '@/features/datasets/utils/improvementModeTableFilters';
import { fetchRankedReviewQueue, recordIdsFromRankedExamples } from '@/features/datasets/utils/fetchRankedReviewQueue';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import {
    MIN_VALIDATIONS_PER_LABEL,
    isReviewGoalMet,
    reviewGoalForCounts,
} from '@/features/datasets/utils/improvementModeReviewGoals';
import '@/features/datasets/components/DataSelection/ImprovementMode.css';

/** Generative tasks (content generation / summarization) — Figma 3968:27495. */
export const GENERATIVE_TOTAL_TITLE = 'Total Examples #';
export const GENERATIVE_REVIEWED_TITLE = 'Reviewed Examples';

/** `1542` → `1,542`, like the Figma widget; unknown counts render as `0`. */
export const formatWidgetCount = (value) =>
    (Number.isFinite(value) ? value : 0).toLocaleString('en-US');

/**
 * Generative tasks have no per-label review goals: the Data Editor shows a
 * single widget with the record total and how many responses were reviewed.
 */
const GenerativeReviewWidget = ({ recordCounts }) => (
    <div className="spark-improvement-mode-generative" data-testid="generative-review-widget">
        <ValidationCard
            type="text-only"
            border
            topTitle={GENERATIVE_TOTAL_TITLE}
            topValue={formatWidgetCount(recordCounts?.total)}
            bottomTitle={GENERATIVE_REVIEWED_TITLE}
            bottomValue={formatWidgetCount(recordCounts?.reviewed)}
            className="spark-improvement-mode-generative-card"
        />
    </div>
);

const ImprovementMode = ({
    allLabelOptions,
    currentUsecase,
    isBenchmark,
    fetchValidationCounts,
    isLoading,
    validationProgress,
    hasLoadedValidationCounts = false,
    onRetrainRequest,
    displayNames,
    entityCounts = {},
    relationCounts = {},
    /** `{ total, reviewed }` from get-label-counts — generative tasks only. */
    recordCounts = null,
    rankScoreInitialized = false,
    currentFilters = {},
    onFilterLabelChange,
    showProTip = true,
    enableTour = true,
    showTourOnOpen = false,
    onViewFilterChange,
    onTourClose,
    focusIncompleteToken = 0,
}) => {
    const [tourVisible, setTourVisible] = useState(false);
    const [reviewCompleteOpen, setReviewCompleteOpen] = useState(false);
    // Baseline of "all goals met" recorded after counts first load, so a tab
    // opened at 100% does not celebrate; null until that first loaded render.
    const prevAllGoalsMetRef = useRef(null);
    // Celebrate at most once per mount even if goals are unmet and re-met.
    const reviewCompleteShownRef = useRef(false);
    const [showDropdown, setShowDropdown] = useState('Entities');
    const [localFilterLabels, setLocalFilterLabels] = useState([]);
    const [filterMenuOpen, setFilterMenuOpen] = useState(false);
    const [showMenuOpen, setShowMenuOpen] = useState(false);
    const filterBarRef = useRef(null);
    const prevShowDropdownRef = useRef(showDropdown);

    useEffect(() => {
        fetchValidationCounts();
    }, [currentUsecase, isBenchmark]);

    useEffect(() => {
        if (!enableTour) return;
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('fromEvaluation') === 'true') {
            setTourVisible(true);
        }
    }, [enableTour]);

    useEffect(() => {
        if (!showTourOnOpen || isLoading) return;
        setTourVisible(true);
    }, [showTourOnOpen, isLoading]);

    const cardRef = useRef(null);

    const handleTourClose = () => {
        setTourVisible(false);
        onTourClose?.();
    };

    const handleValidateClick = (label) => {
        if (tourVisible) setTourVisible(false);
        if (currentFilters.view_label === label) {
            onViewFilterChange?.(null);
            return;
        }
        fetchRankedReviewQueue({
            dataset: isBenchmark ? currentUsecase?.benchmarks?.[0] : currentUsecase?.datasets?.[0],
            label,
            clientId: currentUsecase?.client_id,
            isBenchmark,
            llmUsecaseId: currentUsecase?._id,
            rankScoreInitialized,
            onSuccess: (response) => {
                if (!response?.success) {
                    sparkToast.error('Failed to fetch examples');
                    return;
                }
                onViewFilterChange?.(label, recordIdsFromRankedExamples(response));
            },
            onError: () => {
                sparkToast.error('Failed to fetch examples');
            },
        });
    };

    const isNERTask = currentUsecase?.task_type === TaskType.NER_WITH_RELATIONS || currentUsecase?.task_type === TaskType.NER;
    const isNERTaskWithRelations = currentUsecase?.task_type === TaskType.NER_WITH_RELATIONS;
    const allowMultiFilter = isNERTask || (
        currentUsecase?.task_type === TaskType.classification && Boolean(currentUsecase?.is_multilabel)
    );
    const isSyncedWithTable = typeof onFilterLabelChange === 'function';
    const syncedFilterLabels = tableFiltersToImprovementModeLabels(currentFilters, isNERTask, showDropdown);
    const selectedFilterLabels = isSyncedWithTable
        ? (allowMultiFilter ? syncedFilterLabels : (syncedFilterLabels.length === 1 ? syncedFilterLabels : []))
        : localFilterLabels;
    const improvementFilterKind = isNERTask
        ? (showDropdown === 'Relations' ? 'relations' : 'entities')
        : 'labels';

    // Switching Entities ↔ Relations is a different object type; drop the
    // previous table filter so cards and rows stay on the same selection.
    // Skip the initial mount so we do not wipe a filter applied before render.
    useEffect(() => {
        if (!isNERTaskWithRelations) return;
        if (prevShowDropdownRef.current === showDropdown) return;
        prevShowDropdownRef.current = showDropdown;
        if (isSyncedWithTable) {
            onFilterLabelChange(null, improvementFilterKind);
        } else {
            setLocalFilterLabels([]);
        }
    }, [showDropdown, isNERTaskWithRelations, isSyncedWithTable, onFilterLabelChange, improvementFilterKind]);

    // Outside-click handling is provided by the Spark DropdownMenu itself, which
    // reports closes through `onOpenChange` — including clicks on its portaled
    // overlay panel. A custom filter-bar handler would misfire on the portaled
    // panel (rendered outside the bar) and close the menu before selection.

    let totalValidated, totalRequired, currentLabels, currentCounts, totalObjects;
    let totalRelations = 0;
    let totalEntities = 0;
    let allClassificationGoalsMet = false;

    if (isNERTask) {
        const entityValidated = Object.values(entityCounts).reduce((sum, c) => sum + (c?.validated || 0), 0);
        const relationValidated = Object.values(relationCounts).reduce((sum, c) => sum + (c?.validated || 0), 0);
        totalValidated = showDropdown === 'Entities' ? entityValidated : relationValidated;
        totalEntities = Object.values(entityCounts).reduce((sum, c) => sum + (c?.total || 0), 0);
        totalRelations = Object.values(relationCounts).reduce((sum, c) => sum + (c?.total || 0), 0);
        totalObjects = showDropdown === 'Entities' ? totalEntities : totalRelations;

        const entityRequired = Object.values(entityCounts).reduce((sum, c) => {
            const validated = c?.validated || 0;
            return sum + Math.max(validated, MIN_VALIDATIONS_PER_LABEL);
        }, 0);
        const relationRequired = Object.values(relationCounts).reduce((sum, c) => {
            const validated = c?.validated || 0;
            return sum + Math.max(validated, MIN_VALIDATIONS_PER_LABEL);
        }, 0);
        totalRequired = entityRequired + relationRequired;

        currentLabels = showDropdown === 'Entities' ? Object.keys(entityCounts) : Object.keys(relationCounts);
        currentCounts = showDropdown === 'Entities' ? entityCounts : relationCounts;
    } else {
        const allLabelsFromProgress = Object.keys(validationProgress || {});
        const allLabelsFromOptions = allLabelOptions?.label_names || [];
        const allLabelsSet = new Set([...allLabelsFromProgress, ...allLabelsFromOptions]);
        const allLabels = Array.from(allLabelsSet);

        const labelsWithCounts = allLabels.filter(label => (validationProgress[label]?.total || 0) > 0);

        currentLabels = labelsWithCounts;
        currentCounts = validationProgress;

        totalValidated = labelsWithCounts.reduce((sum, label) => sum + (validationProgress[label]?.validated || 0), 0);
        totalObjects = labelsWithCounts.reduce((sum, label) => sum + (validationProgress[label]?.total || 0), 0);
        totalRequired = labelsWithCounts.reduce((sum, label) => {
            const validated = validationProgress[label]?.validated || 0;
            return sum + Math.max(validated, reviewGoalForCounts(validationProgress[label]));
        }, 0);
        allClassificationGoalsMet = labelsWithCounts.length > 0
            && labelsWithCounts.every((label) => isReviewGoalMet(validationProgress[label]));
    }

    if (selectedFilterLabels.length > 0) {
        currentLabels = currentLabels.filter((label) => selectedFilterLabels.includes(label));
    }

    const overallPercent = totalRequired === 0 ? 0 : Math.min(100, (totalValidated / totalRequired) * 100);

    // "Way to go!" celebration: fires only at the moment every label review
    // goal is first met during this visit (never when the tab loads already
    // complete), and only once per mount. Classification-only — the Overall
    // Progress percentage does not exist for NER tasks — and only where a
    // retrain hand-off is available (the task-page Data Editor tab).
    const canCelebrateReviewComplete = currentUsecase?.task_type === TaskType.classification
        && typeof onRetrainRequest === 'function';
    useEffect(() => {
        if (!canCelebrateReviewComplete || !hasLoadedValidationCounts) return;
        if (prevAllGoalsMetRef.current === null) {
            prevAllGoalsMetRef.current = allClassificationGoalsMet;
            return;
        }
        if (!prevAllGoalsMetRef.current && allClassificationGoalsMet && !reviewCompleteShownRef.current) {
            reviewCompleteShownRef.current = true;
            setReviewCompleteOpen(true);
        }
        prevAllGoalsMetRef.current = allClassificationGoalsMet;
    }, [canCelebrateReviewComplete, hasLoadedValidationCounts, allClassificationGoalsMet]);

    const getAllAvailableLabels = () => {
        if (isNERTask) {
            return Object.keys(showDropdown === 'Entities' ? entityCounts : relationCounts);
        }
        const allLabelsFromProgress = Object.keys(validationProgress || {});
        const allLabelsFromOptions = allLabelOptions?.label_names || [];
        return Array.from(new Set([...allLabelsFromProgress, ...allLabelsFromOptions])).filter(
            label => (validationProgress[label]?.total || 0) > 0
        );
    };

    const availableLabels = getAllAvailableLabels();

    const getAllLabelText = () => {
        if (currentUsecase?.task_type === TaskType.classification) return 'All Labels';
        if (currentUsecase?.task_type === TaskType.NER) return 'All Entities';
        if (currentUsecase?.task_type === TaskType.NER_WITH_RELATIONS) {
            return showDropdown === 'Entities' ? 'All Entities' : 'All Relations';
        }
        return 'All Labels';
    };

    const allLabelText = getAllLabelText();

    const applyFilter = (value) => {
        if (!isSyncedWithTable) {
            setLocalFilterLabels(Array.isArray(value) ? value : (value ? [value] : []));
        }
        if (!allowMultiFilter) {
            setFilterMenuOpen(false);
        }
        onFilterLabelChange?.(value, improvementFilterKind);
    };
    const toggleFilterLabel = (label) => {
        const next = selectedFilterLabels.includes(label)
            ? selectedFilterLabels.filter((item) => item !== label)
            : [...selectedFilterLabels, label];
        applyFilter(next.length === 0 ? null : next);
    };
    const filterItems = allowMultiFilter
        ? [
            {
                label: allLabelText,
                type: 'checkbox',
                checked: selectedFilterLabels.length === 0,
                onClick: () => applyFilter(null),
            },
            ...availableLabels.map((label) => ({
                label,
                type: 'checkbox',
                checked: selectedFilterLabels.includes(label),
                onClick: () => toggleFilterLabel(label),
            })),
        ]
        : [
            {
                label: allLabelText,
                state: selectedFilterLabels.length === 0 ? 'selected' : 'default',
                onClick: () => applyFilter(null),
            },
            ...availableLabels.map((label) => ({
                label,
                state: selectedFilterLabels.includes(label) ? 'selected' : 'default',
                onClick: () => applyFilter(label),
            })),
        ];
    const filterText = `Filter: ${selectedFilterLabels[0] ?? allLabelText}`;
    const visibleFilterTags = selectedFilterLabels.slice(0, 1);
    const filterOverflowCount = Math.max(0, selectedFilterLabels.length - 1);

    const selectShow = (value) => {
        setShowDropdown(value);
        setShowMenuOpen(false);
    };
    const showItems = ['Entities', 'Relations'].map(value => ({
        label: value,
        state: showDropdown === value ? 'selected' : 'default',
        onClick: () => selectShow(value),
    }));

    // Derive the allLabels list used for stable color assignment.
    const allLabelsForColor = isNERTask
        ? (allLabelOptions?.entity_names || Object.keys(showDropdown === 'Entities' ? entityCounts : relationCounts))
        : (allLabelOptions?.label_names || []);

    // Build the cards array for the carousel.
    const buildCarouselCards = () => {
        const bottomTitle = isNERTask
            ? (showDropdown === 'Entities' ? 'Entities Validated' : 'Relations Validated')
            : 'Reviewed';

        const overallCard = isNERTask
            ? {
                type: 'text-only',
                border: true,
                topTitle: 'Overview / Total extractions',
                topValue: String(totalObjects),
                bottomTitle,
                bottomValue: String(totalValidated),
            }
            : {
                type: 'text-only',
                border: true,
                topTitle: 'Overall Progress',
                topValue: String(totalObjects),
                bottomTitle: 'Labels reviewed',
                bottomValue: `${totalValidated}/${totalRequired}`,
                showProgress: true,
                progressValue: Math.round(overallPercent),
                progressType: allClassificationGoalsMet ? 'success' : 'default',
            };

        const perLabelCards = currentLabels.map(label => {
            const displayLabel = displayNames?.[label] || label;
            const validated = currentCounts[label]?.validated || 0;
            const total = currentCounts[label]?.total || 0;
            const minRequired = reviewGoalForCounts(currentCounts[label]);
            const progressPercent = Math.min(100, (validated / minRequired) * 100);

            if (isNERTask) {
                return {
                    type: 'tag',
                    border: true,
                    tagLabel: displayLabel,
                    tagVariant: getSparkTagVariant(label, allLabelsForColor),
                    tagTopValue: String(total),
                    bottomTitle,
                    bottomValue: String(validated),
                };
            }

            return {
                type: 'tag',
                border: true,
                tagLabel: displayLabel,
                tagVariant: getSparkTagVariant(label, allLabelsForColor),
                tagTopValue: String(total),
                bottomTitle: 'Reviewed',
                bottomValue: `${validated}/${minRequired}`,
                showReviewButton: true,
                reviewLabel: 'View',
                reviewDisabled: !rankScoreInitialized && validated < 5,
                reviewActive: currentFilters.view_label === label,
                onReviewClick: () => handleValidateClick(label),
                showProgress: true,
                progressValue: Math.round(progressPercent),
                progressType: isReviewGoalMet(currentCounts[label]) ? 'success' : 'default',
            };
        });

        // Guided walkthrough ("Focus on one label") anchors to the View button
        // of the first card whose button can actually be clicked.
        const walkthroughCard =
            perLabelCards.find((card) => card.showReviewButton && !card.reviewDisabled) ||
            perLabelCards.find((card) => card.showReviewButton);
        if (walkthroughCard) walkthroughCard.className = 'walkthrough-label-card';

        return [overallCard, ...perLabelCards];
    };

    const carouselCards = buildCarouselCards();
    const firstIncompleteIndex = currentLabels.findIndex(
        (label) => !isReviewGoalMet(currentCounts[label]),
    );
    const scrollIntoViewIndex = firstIncompleteIndex >= 0 ? firstIncompleteIndex + 1 : null;

    // Content generation / summarization: only the totals widget — no label
    // filter, per-label cards, Pro Tip, tour or celebration (Figma 3968:27495).
    if (isGenerativeTask(currentUsecase?.task_type)) {
        return isLoading ? (
            <div className="spark-improvement-mode-spinner-wrap">
                <Spinner size="large" />
            </div>
        ) : (
            <GenerativeReviewWidget recordCounts={recordCounts} />
        );
    }

    return (
        <>
            {isLoading ? (
                <div className="spark-improvement-mode-spinner-wrap">
                    <Spinner size="large" />
                </div>
            ) : (
                <div ref={cardRef} className="improvement-mode-tour-target">
                    {/* Filter bar — kept outside the overflow:hidden wrapper so the
                        Spark DropdownMenu overlay panel is not clipped. */}
                    <div className="spark-improvement-mode-filter-bar" ref={filterBarRef}>
                        <DropdownMenu
                            size="small"
                            selectType={allowMultiFilter ? 'multi-select' : 'single-select'}
                            showLabel={false}
                            panelLayout="overlay"
                            text={allowMultiFilter ? undefined : filterText}
                            placeholder={allowMultiFilter ? `Filter: ${allLabelText}` : undefined}
                            tags={allowMultiFilter ? visibleFilterTags : undefined}
                            overflowCount={allowMultiFilter ? filterOverflowCount : undefined}
                            items={filterItems}
                            open={filterMenuOpen}
                            onOpenChange={setFilterMenuOpen}
                            searchable={!allowMultiFilter}
                            searchMode="trigger"
                            searchEmptyText={`No matching ${allLabelText.replace(/^All /, '').toLowerCase()}`}
                            className="spark-improvement-mode-filter-dropdown"
                            panelClassName="spark-improvement-mode-filter-panel"
                        />

                        {isNERTaskWithRelations && (
                            <DropdownMenu
                                size="small"
                                selectType="single-select"
                                showLabel={false}
                                panelLayout="overlay"
                                text={`Show: ${showDropdown}`}
                                items={showItems}
                                open={showMenuOpen}
                                onOpenChange={setShowMenuOpen}
                                className="spark-improvement-mode-filter-dropdown"
                                panelClassName="spark-improvement-mode-filter-panel"
                            />
                        )}
                    </div>

                    <div className="spark-improvement-mode-carousel-wrap">
                        {/* The Data Editor guide's first mark ("Review & Annotate
                            your examples", Figma 9530:10856) outlines the review
                            progress cards; the carousel root only takes a class. */}
                        <div className="spark-improvement-mode-carousel-anchor" data-walkthrough="data-editor-table">
                            <ValidationCardCarousel
                                items={carouselCards}
                                cardClassName="spark-improvement-mode-card"
                                scrollIntoViewIndex={scrollIntoViewIndex}
                                scrollIntoViewNonce={focusIncompleteToken}
                            />
                        </div>

                        {!isNERTask && showProTip && (
                            <SparkNotification
                                wide
                                feedback="info"
                                title="Pro Tip"
                                content={`Review at least ${MIN_VALIDATIONS_PER_LABEL} examples of each label for optimal model performance. The more you review, the better your model will perform!`}
                                showCloseButton={false}
                                showButton={false}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* The tour measures `cardRef` when `open` flips on, so it must not
                open while the spinner is shown and the card is not in the DOM
                (e.g. arriving from the Evaluations "Improve" button while
                validation counts are still loading). */}
            <ImprovementModeTour
                cardRef={cardRef}
                minValidationsPerLabel={MIN_VALIDATIONS_PER_LABEL}
                open={tourVisible && !isLoading}
                onClose={handleTourClose}
            />

            <ReviewCompleteModal
                open={reviewCompleteOpen}
                onClose={() => setReviewCompleteOpen(false)}
                onRetrainClick={() => {
                    setReviewCompleteOpen(false);
                    onRetrainRequest?.();
                }}
            />
        </>
    );
};

export default ImprovementMode;
